import React, { useState, useEffect, useCallback } from 'react';
import { getTelas, createTela, updateTela, deleteTela } from '../../services/muestrasApi';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { toast } from 'sonner';
import { Shirt, Plus, Edit2, Trash2, X, Search, GripVertical, AlertTriangle } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SortableRow = ({ item, canEdit, canDelete, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id_tela });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const showActions = canEdit || canDelete;

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50 border-b">
      {canEdit && (
        <td className="px-3 py-3 w-10">
          <button {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600">
            <GripVertical className="w-4 h-4" />
          </button>
        </td>
      )}
      <td className="px-4 py-3 font-mono text-sm text-gray-600">{item.id_tela}</td>
      <td className="px-4 py-3 font-semibold text-gray-800">{item.nombre || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.gramaje || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.proveedor || '-'}</td>
      <td className="px-4 py-3 text-sm">
        {item.color && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{item.color}</span>}
        {!item.color && '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.clasificacion || '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{item.precio ? `S/ ${parseFloat(item.precio).toFixed(2)}` : '-'}</td>
      {showActions && (
        <td className="px-4 py-3">
          <div className="flex justify-center gap-1">
            {canEdit && (
              <button 
                onClick={() => onEdit(item)} 
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                data-testid={`edit-tela-${item.id_tela}`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button 
                onClick={() => onDelete(item)} 
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid={`delete-tela-${item.id_tela}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

const MuestrasTelas = ({ muestrasUser, muestrasIsAdmin }) => {
  // Usar el nuevo sistema de permisos
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Telas
  const puedeCrear = canCreate('telas');
  const puedeEditar = canEdit('telas');
  const puedeEliminar = canDelete('telas');
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({ 
    nombre: '', gramaje: '', elasticidad: '', proveedor: '', 
    ancho_estandar: '', color: '', clasificacion: '', precio: '' 
  });
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [clasificaciones, setClasificaciones] = useState([]);
  
  const COLORES = ['Azul', 'Negro', 'Crudo', 'Color'];

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    // Extraer clasificaciones únicas del historial
    const uniqueClasif = [...new Set(data.map(t => t.clasificacion).filter(Boolean))];
    setClasificaciones(uniqueClasif);
  }, [data]);

  const loadData = async () => {
    setLoading(true);
    try { 
      const telas = await getTelas();
      setData((telas || []).sort((a, b) => (a.orden || 0) - (b.orden || 0)));
    } 
    catch (error) { toast.error('Error cargando telas'); } 
    finally { setLoading(false); }
  };

  const filteredData = React.useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(item => 
      item.nombre?.toLowerCase().includes(query) ||
      item.proveedor?.toLowerCase().includes(query) ||
      item.color?.toLowerCase().includes(query)
    );
  }, [data, searchQuery]);

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setFormData({ 
      nombre: item?.nombre || '', gramaje: item?.gramaje || '', 
      elasticidad: item?.elasticidad || '', proveedor: item?.proveedor || '', 
      ancho_estandar: item?.ancho_estandar || '', color: item?.color || '', 
      clasificacion: item?.clasificacion || '', precio: item?.precio || '' 
    });
    setShowModal(true);
  }, []);

  const handleSubmit = async () => {
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    try {
      if (editingItem) {
        await updateTela(editingItem.id_tela, formData);
        toast.success('Tela actualizada');
      } else {
        await createTela(formData);
        toast.success('Tela creada');
      }
      setShowModal(false);
      loadData();
    } catch (error) { toast.error(error.message); }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try { 
      await deleteTela(deleteItem.id_tela); 
      toast.success('Tela eliminada'); 
      setDeleteItem(null);
      loadData(); 
    } catch (error) { 
      toast.error(error.message); 
    } finally {
      setDeleting(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex(item => item.id_tela === active.id);
    const newIndex = data.findIndex(item => item.id_tela === over.id);
    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);

    try {
      const token = localStorage.getItem('token');
      const items = newData.map((item, idx) => ({ id_tela: item.id_tela, orden: idx }));
      await fetch(`${API_BASE}/api/muestras/telas/reorder`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items })
      });
      toast.success('Orden actualizado');
    } catch (error) {
      toast.error('Error al guardar orden');
      loadData();
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-telas-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-pink-100 rounded-xl">
            <Shirt className="w-6 h-6 text-pink-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Telas</h1>
            <p className="text-gray-400 text-sm">Gestión del catálogo de telas {puedeEditar && '• Arrastra para reordenar'}</p>
          </div>
        </div>
        {puedeCrear && (
          <button onClick={() => openModal()} className="flex items-center gap-2 px-4 py-2.5 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-colors font-medium">
            <Plus className="w-4 h-4" />
            Agregar
          </button>
        )}
      </div>

      <div className="mb-4">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar tela..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filteredData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay telas registradas</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {puedeEditar && <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-10"></th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-16">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">NOMBRE</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-24">GRAMAJE</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">PROVEEDOR</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-24">COLOR</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">CLASIFICACIÓN</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-24">PRECIO</th>
                  {(puedeEditar || puedeEliminar) && <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase w-28">ACCIONES</th>}
                </tr>
              </thead>
              <SortableContext items={filteredData.map(item => item.id_tela)} strategy={verticalListSortingStrategy}>
                <tbody>
                  {filteredData.map(item => (
                    <SortableRow key={item.id_tela} item={item} canEdit={puedeEditar} canDelete={puedeEliminar} onEdit={openModal} onDelete={setDeleteItem} />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        )}
      </div>

      {/* Modal de Crear/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{editingItem ? 'Editar Tela' : 'Nueva Tela'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gramaje</label>
                  <input type="text" value={formData.gramaje} onChange={(e) => setFormData({ ...formData, gramaje: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Elasticidad</label>
                  <input type="text" value={formData.elasticidad} onChange={(e) => setFormData({ ...formData, elasticidad: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
                  <input type="text" value={formData.proveedor} onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ancho (cm)</label>
                  <input type="number" step="0.01" value={formData.ancho_estandar} onChange={(e) => setFormData({ ...formData, ancho_estandar: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <select 
                    value={formData.color} 
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white"
                  >
                    <option value="">Seleccionar...</option>
                    {COLORES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Precio (S/)</label>
                  <input type="number" step="0.01" value={formData.precio} onChange={(e) => setFormData({ ...formData, precio: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Clasificación</label>
                <input 
                  type="text" 
                  list="clasificaciones-list"
                  value={formData.clasificacion} 
                  onChange={(e) => setFormData({ ...formData, clasificacion: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Escribe o selecciona..."
                />
                <datalist id="clasificaciones-list">
                  {clasificaciones.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">
                Cancelar
              </button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 font-medium">
                {editingItem ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Tela?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar <strong>"{deleteItem.nombre}"</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasTelas;
